import { useState } from 'react';
import { 
  updateUserProfile, 
  uploadProfilePicture, 
  uploadCoverPicture,
  updateUserExperience,
  updateUserEducation,
  updateUsername
} from '../api/users';

/**
 * Hook for user profile management operations
 * @param {Object} initialUser - Initial user data
 * @param {Function} onUserUpdate - Callback for when user is updated
 * @returns {Object} User management methods and state
 */
const useUser = (initialUser, onUserUpdate) => {
  const [loading, setLoading] = useState({
    profile: false,
    profilePicture: false,
    coverPicture: false,
    experience: false,
    education: false,
    username: false,
  });
  const [error, setError] = useState({
    profile: '',
    profilePicture: '',
    coverPicture: '',
    experience: '',
    education: '',
    username: '',
  });
  const [uploadProgress, setUploadProgress] = useState({
    profilePicture: 0,
    coverPicture: 0,
  });

  // Update general profile information
  const updateProfile = async (profileData) => {
    try {
      setLoading(prev => ({ ...prev, profile: true }));
      setError(prev => ({ ...prev, profile: '' }));

      const response = await updateUserProfile(profileData);
      const updatedUser = response.data.data.user;
      
      onUserUpdate && onUserUpdate(updatedUser);
      return updatedUser;
    } catch (err) {
      const errorMessage = 
        err.response?.data?.message || 
        err.response?.data?.errors?.[0]?.message || 
        'Failed to update profile. Please try again.';
      
      setError(prev => ({ ...prev, profile: errorMessage }));
      throw err;
    } finally {
      setLoading(prev => ({ ...prev, profile: false }));
    }
  };

  // Update username
  const changeUsername = async (username) => {
    try {
      setLoading(prev => ({ ...prev, username: true }));
      setError(prev => ({ ...prev, username: '' }));

      const response = await updateUsername(username);
      const { user, token } = response.data.data;
      
      // Save new token
      localStorage.setItem('token', token);
      
      onUserUpdate && onUserUpdate(user);
      return user;
    } catch (err) {
      const errorMessage = 
        err.response?.data?.message || 
        err.response?.data?.errors?.[0]?.message || 
        'Failed to update username. Please try again.';
      
      setError(prev => ({ ...prev, username: errorMessage }));
      throw err;
    } finally {
      setLoading(prev => ({ ...prev, username: false }));
    }
  };

  // Upload profile picture
  const uploadProfileImage = async (image) => {
    try {
      setLoading(prev => ({ ...prev, profilePicture: true }));
      setError(prev => ({ ...prev, profilePicture: '' }));
      setUploadProgress(prev => ({ ...prev, profilePicture: 0 }));

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(image.type)) {
        throw new Error('File type not supported. Please use JPEG, PNG, or WebP format.');
      }

      // Validate file size (max 2MB)
      const maxSize = 2 * 1024 * 1024; // 2MB in bytes
      if (image.size > maxSize) {
        throw new Error('File size too large. Maximum size is 2MB.');
      }

      const onProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(prev => ({ ...prev, profilePicture: percentCompleted }));
      };

      const response = await uploadProfilePicture(image, onProgress);
      const updatedUser = response.data.data.user;
      
      onUserUpdate && onUserUpdate(updatedUser);
      return updatedUser;
    } catch (err) {
      console.error('Profile picture upload error details:', err);
      
      // Extract more detailed error message if available
      let errorMessage = 'Failed to upload profile picture. Please try again.';
      
      if (err.message) {
        errorMessage = err.message;
      } else if (err.response) {
        // Log detailed response for debugging
        console.error('Error response:', err.response);
        
        if (err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data && err.response.data.errors && err.response.data.errors.length > 0) {
          errorMessage = err.response.data.errors[0].message;
        }
      }
      
      setError(prev => ({ ...prev, profilePicture: errorMessage }));
      throw err;
    } finally {
      setLoading(prev => ({ ...prev, profilePicture: false }));
    }
  };

  // Upload cover picture
  const uploadCoverImage = async (image) => {
    try {
      setLoading(prev => ({ ...prev, coverPicture: true }));
      setError(prev => ({ ...prev, coverPicture: '' }));
      setUploadProgress(prev => ({ ...prev, coverPicture: 0 }));

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(image.type)) {
        throw new Error('File type not supported. Please use JPEG, PNG, or WebP format.');
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (image.size > maxSize) {
        throw new Error('File size too large. Maximum size is 5MB.');
      }

      const onProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(prev => ({ ...prev, coverPicture: percentCompleted }));
      };

      const response = await uploadCoverPicture(image, onProgress);
      const updatedUser = response.data.data.user;
      
      onUserUpdate && onUserUpdate(updatedUser);
      return updatedUser;
    } catch (err) {
      console.error('Cover picture upload error details:', err);
      
      // Extract more detailed error message if available
      let errorMessage = 'Failed to upload cover picture. Please try again.';
      
      if (err.message) {
        errorMessage = err.message;
      } else if (err.response) {
        // Log detailed response for debugging
        console.error('Error response:', err.response);
        
        if (err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data && err.response.data.errors && err.response.data.errors.length > 0) {
          errorMessage = err.response.data.errors[0].message;
        }
      }
      
      setError(prev => ({ ...prev, coverPicture: errorMessage }));
      throw err;
    } finally {
      setLoading(prev => ({ ...prev, coverPicture: false }));
    }
  };

  // Update experience
  const updateExperience = async (experience) => {
    try {
      setLoading(prev => ({ ...prev, experience: true }));
      setError(prev => ({ ...prev, experience: '' }));

      const response = await updateUserExperience(experience);
      const updatedUser = response.data.data.user;
      
      onUserUpdate && onUserUpdate(updatedUser);
      return updatedUser;
    } catch (err) {
      const errorMessage = 
        err.response?.data?.message || 
        err.response?.data?.errors?.[0]?.message || 
        'Failed to update experience. Please try again.';
      
      setError(prev => ({ ...prev, experience: errorMessage }));
      throw err;
    } finally {
      setLoading(prev => ({ ...prev, experience: false }));
    }
  };

  // Update education
  const updateEducation = async (education) => {
    try {
      setLoading(prev => ({ ...prev, education: true }));
      setError(prev => ({ ...prev, education: '' }));

      const response = await updateUserEducation(education);
      const updatedUser = response.data.data.user;
      
      onUserUpdate && onUserUpdate(updatedUser);
      return updatedUser;
    } catch (err) {
      const errorMessage = 
        err.response?.data?.message || 
        err.response?.data?.errors?.[0]?.message || 
        'Failed to update education. Please try again.';
      
      setError(prev => ({ ...prev, education: errorMessage }));
      throw err;
    } finally {
      setLoading(prev => ({ ...prev, education: false }));
    }
  };

  return {
    loading,
    error,
    uploadProgress,
    updateProfile,
    changeUsername,
    uploadProfileImage,
    uploadCoverImage,
    updateExperience,
    updateEducation
  };
};

export default useUser;
